import { AnimationProps, Controller, ControllerUpdate, SpringConfig, SpringRef, SpringValues } from '@react-spring/web';
import React from 'react';
import {
  BlockDragPosition,
  BlockDragState,
  BlockRenderElement,
  LayoutStage,
  Rect,
  RenderElement,
  RenderElementState,
  ResizeDragState,
} from '../types';
import fastDeepEqual from 'fast-deep-equal';
import { sortBy } from 'lodash';
import { useRefCallback } from 'src/utils/react/ref-callback.hook';
import { Nullable } from 'src/types/nullable.type';
import { diffItems } from './diff-items';

const keyFn = (item: RenderElement) => item.id;

function pushBlockControllerUpdate(
  controller: Controller<RenderElementState>,
  props: RenderElementState,
  animationProps: AnimationProps<RenderElementState>
) {
  let started = 0;
  let skipped = 0;
  const updates: Partial<RenderElementState> = {};
  const springs = controller.springs;
  for (const key in props) {
    const rKey = key as keyof RenderElementState;
    if (rKey in springs && springs[rKey].goal !== props[rKey]) {
      updates[rKey] = props[rKey];
      started++;
    } else {
      skipped++;
    }
  }
  controller.start({
    ...updates,
    ...animationProps,
  });
  return { started, skipped };
}

const ANIMATION_CONFIG: SpringConfig = {
  mass: 1,
  tension: 400,
  friction: 40,
};

export function useAnimatedRenderElements(
  renderElements: RenderElement[],
  focusedBlockId: Nullable<string>,
  resizeDragState: Nullable<ResizeDragState>,
  lastActionWasUndoOrRedo: boolean,
  layoutStage: LayoutStage
) {
  const prevRenderElementsRef = React.useRef<RenderElement[]>([]);
  const controllersRef = React.useRef<Map<string, Controller<RenderElementState>>>(new Map());
  const renderElementsRectMapRef = React.useRef<Map<string, Rect>>(new Map());

  const dragStateRef = React.useRef<Nullable<BlockDragState>>(null);
  const dragPositionRef = React.useRef<Nullable<BlockDragPosition>>(null);

  const sortedRenderElements = React.useMemo(() => sortBy(renderElements, ['re.type', 'id']), [renderElements]);

  const focusedBlockRe = React.useMemo(
    () => sortedRenderElements.find((re) => re.type === 'block' && re.block.id === focusedBlockId),
    [sortedRenderElements, focusedBlockId]
  );
  const textBlockHasFocus = Boolean(
    focusedBlockRe && (focusedBlockRe as BlockRenderElement).block.content.type === 'text'
  );
  const isResizing = Boolean(resizeDragState?.boundary);

  const handledLastActionWasUndoOrRedo = React.useRef(true);

  if (lastActionWasUndoOrRedo && handledLastActionWasUndoOrRedo.current) {
    handledLastActionWasUndoOrRedo.current = false;
  }

  const waitingStageSpringsRef = React.useRef<{ [key: string]: RenderElementState }>({});

  const initialLoadCompleteRef = React.useRef(false);

  const springs = React.useMemo(() => {
    // console.log('useAnimatedRenderElements', 'springs', sortedRenderElements);

    if (layoutStage === 'waiting-for-sizes') {
      return sortedRenderElements.map((re) => {
        const id = re.id;
        // We do this so that we return the same object per id
        if (!waitingStageSpringsRef.current[id]) {
          waitingStageSpringsRef.current[id] = {
            x: 0,
            y: 0,
            width: re.rect.width,
            height: 0,
            opacity: 1,
            scale: 1,
          };
        } else {
          waitingStageSpringsRef.current[id].width = re.rect.width;
        }
        return waitingStageSpringsRef.current[id];
      });
    }

    const prevRenderElements = prevRenderElementsRef.current;
    renderElementsRectMapRef.current = new Map(sortedRenderElements.map((re) => [re.id, re.rect]));

    // console.log('useAnimatedRenderElements', 'prevRenderElements', prevRenderElements);
    const { added, removed, updated } = diffItems(prevRenderElements, sortedRenderElements, keyFn);

    // console.log(
    //   'useAnimatedRenderElements',
    //   'added',
    //   added.length,
    //   'removed',
    //   removed.length,
    //   'updated',
    //   updated.length
    // );

    prevRenderElementsRef.current = sortedRenderElements;

    for (const re of removed) {
      // console.log('useAnimatedRenderElements', 'removed', re);
      const id = re.id;
      const controller = controllersRef.current.get(id);
      if (controller) {
        controller.each((spring) => spring.stop());
        controllersRef.current.delete(id);
      }
    }

    for (const re of added) {
      // console.log('useAnimatedRenderElements', 'added', re);
      const id = re.id;
      const spring = SpringRef<RenderElementState>();
      const controller = new Controller({
        ref: spring,
        from: {
          opacity: 1,
          scale: 0.8,
          x: re.rect.x,
          y: re.rect.y,
          width: re.rect.width,
          height: re.rect.height,
        },
      });
      controllersRef.current.set(id, controller);
      pushBlockControllerUpdate(
        controller,
        {
          x: re.rect.x,
          y: re.rect.y,
          width: re.rect.width,
          height: re.rect.height,
          scale: 1,
          opacity: 1,
        },
        {
          config: ANIMATION_CONFIG,
          immediate: !initialLoadCompleteRef.current,
        }
      );
    }

    for (const re of updated) {
      // console.log('useAnimatedRenderElements', 'updated', re);
      const id = re.id;
      const controller = controllersRef.current.get(id);
      if (controller) {
        const dragState = dragStateRef.current;
        const isDragging = dragState?.blockId === re.id && dragState.dragged && !dragState.stopped;
        if (isDragging) {
          // console.log('useAnimatedRenderElements', 'isDragging', re);
        }
        const { started, skipped } = pushBlockControllerUpdate(
          controller,
          {
            x: isDragging ? re.rect.x + dragPositionRef.current!.dx : re.rect.x,
            y: isDragging ? re.rect.y + dragPositionRef.current!.dy : re.rect.y,
            width: re.rect.width,
            height: re.rect.height,
            scale: isDragging ? 0.8 : 1,
            opacity: isDragging ? 0.8 : 1,
          },
          {
            config: ANIMATION_CONFIG,
            immediate:
              textBlockHasFocus || isResizing || (lastActionWasUndoOrRedo && !handledLastActionWasUndoOrRedo.current),
          }
        );

        // console.log('useAnimatedRenderElements', 'updated', `started: ${started}, skipped: ${skipped}`);
      }
    }

    return sortedRenderElements.map((re) => {
      const id = re.id;
      const controller = controllersRef.current.get(id);
      if (!controller) {
        throw new Error('Controller not found for render element: ' + id);
      }
      return controller.springs;
    });
  }, [sortedRenderElements, textBlockHasFocus, isResizing, layoutStage]);

  if (initialLoadCompleteRef.current === false && layoutStage === 'ready') {
    initialLoadCompleteRef.current = true;
  }

  if (lastActionWasUndoOrRedo && !handledLastActionWasUndoOrRedo.current) {
    handledLastActionWasUndoOrRedo.current = true;
  }

  const startDragElement = useRefCallback(
    (id: string, blockDragState: BlockDragState, blockDragPosition: BlockDragPosition) => {
      const controller = controllersRef.current.get(id);
      if (!controller) {
        throw new Error('Controller not found for render element: ' + id);
      }
      dragStateRef.current = blockDragState;
      dragPositionRef.current = blockDragPosition;
      const rect = renderElementsRectMapRef.current.get(id)!;
      pushBlockControllerUpdate(
        controller,
        {
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
          scale: 1,
          opacity: 1,
        },
        {
          config: ANIMATION_CONFIG,
        }
      );
    },
    []
  );

  const dragElement = useRefCallback(
    (id: string, blockDragState: BlockDragState, blockDragPosition: BlockDragPosition) => {
      // console.log('useAnimatedRenderElements', 'dragElement', id, blockDragState, blockDragPosition);
      const controller = controllersRef.current.get(id);
      if (!controller) {
        throw new Error('Controller not found for render element: ' + id);
      }
      dragStateRef.current = blockDragState;
      dragPositionRef.current = blockDragPosition;
      const rect = renderElementsRectMapRef.current.get(id)!;
      const isDragging = blockDragState.blockId === id && blockDragState.dragged && !blockDragState.stopped;
      pushBlockControllerUpdate(
        controller,
        {
          x: rect.x + blockDragPosition.dx,
          y: rect.y + blockDragPosition.dy,
          width: rect.width,
          height: rect.height,
          scale: isDragging ? 0.8 : 1,
          opacity: isDragging ? 0.8 : 1,
        },
        {
          config: ANIMATION_CONFIG,
          immediate: true,
        }
      );
    },
    []
  );

  const releaseElement = useRefCallback(
    (id: string, blockDragState: BlockDragState, blockDragPosition: BlockDragPosition) => {
      // console.log('useAnimatedRenderElements', 'releaseElement', 'start');
      const controller = controllersRef.current.get(id);
      if (!controller) {
        throw new Error('Controller not found for render element: ' + id);
      }
      dragStateRef.current = blockDragState;
      dragPositionRef.current = blockDragPosition;
      const rect = renderElementsRectMapRef.current.get(id)!;
      pushBlockControllerUpdate(
        controller,
        {
          x: rect.x,
          y: rect.y,
          width: rect.width,
          height: rect.height,
          scale: 1,
          opacity: 1,
        },
        {
          config: ANIMATION_CONFIG,
        }
      );
    },
    []
  );

  return { sortedRenderElements, springs, startDragElement, dragElement, releaseElement };
}
